/**
 * IntroTour — first-login spotlight walkthrough of the app.
 *
 * Steps reference `[data-tour="..."]` attributes on real DOM elements so
 * the tour never depends on fragile selectors. The spotlight is an SVG
 * cutout positioned per frame from the live `getBoundingClientRect()` of
 * the target — pixel-exact regardless of viewport, scroll, or resize.
 *
 * Persistence: localStorage flag (first-device-only per Owen's call).
 * Re-invoke from Settings → Walkthrough.
 *
 * Public surface:
 *   <IntroTour /> — mount once near the app root.
 *   triggerIntroTour() — programmatic start, used by Settings replay.
 *   hasSeenIntroTour() — read the flag.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./IntroTour.css";

// ── Persistence ────────────────────────────────────────────

const FLAG_KEY = "foreman_intro_tour_done";

export function hasSeenIntroTour(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return true; // fail-closed: if we can't read storage, don't auto-fire
  }
}

function markSeen() {
  try {
    localStorage.setItem(FLAG_KEY, "1");
  } catch {}
}

/** Programmatic start. Dispatches a window event the IntroTour listens for. */
export function triggerIntroTour() {
  window.dispatchEvent(new CustomEvent("foreman:intro-tour:start"));
}

/** For Settings replay — clears the seen flag so future logins also re-show. */
export function resetIntroTourFlag() {
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {}
}

// ── Step definitions ──────────────────────────────────────

type Side = "top" | "bottom" | "left" | "right";

interface TourStep {
  selector: string;
  title: string;
  body: string;
  /** Where to place the Foreman bubble relative to the target. */
  side: Side;
  /** Navigate here before the step renders, if not already on this path. */
  goto?: string;
  /** Round-rect radius applied to the cutout. Defaults to 12. */
  radius?: number;
  /** Outer padding around the target before the cutout is drawn. Defaults to 6. */
  padding?: number;
  /** Final step — instead of "Got it" CTA, prompts the user to type. */
  isFinal?: boolean;
}

const STEPS: TourStep[] = [
  {
    selector: '[data-tour="sidebar"]',
    title: "The sidebar.",
    body: "This is the only place you'll go. Five pages. I'll show you each.",
    side: "right",
    goto: "/app/chat",
    radius: 16,
    padding: 8,
  },
  {
    selector: '[data-tour="sidebar-chat"]',
    title: "Chat.",
    body: "Talk to me here. Ask anything. Tell me to do anything. I'll show my working.",
    side: "right",
  },
  {
    selector: '[data-tour="sidebar-actions"]',
    title: "Actions.",
    body: "Tell me once, I'll keep doing it. Quote approved? I raise the job, draft the email, prep the materials list.",
    side: "right",
  },
  {
    selector: '[data-tour="sidebar-reports"]',
    title: "Reports.",
    body: "Want a weekly schedule check at eight on Monday? Tell me, I deliver it.",
    side: "right",
  },
  {
    selector: '[data-tour="sidebar-lookouts"]',
    title: "Lookouts.",
    body: "I watch for things you'd miss. Overdue invoices, empty engineer diaries, unanswered customers. I bring them to you before they bite.",
    side: "right",
  },
  {
    selector: '[data-tour="sidebar-audit"]',
    title: "Audit.",
    body: "Everything I've done is here. Every email I drafted, every job I raised. Look in any time.",
    side: "right",
  },
  {
    selector: '[data-tour="chat-input"]',
    title: "Now your turn.",
    body: "Ask me anything.",
    side: "top",
    radius: 14,
    padding: 10,
    isFinal: true,
  },
];

// ── Position helpers ──────────────────────────────────────

interface Rect {
  top: number; left: number; width: number; height: number;
}

const VP_MARGIN = 16; // keep the bubble at least this far from the viewport edge
const BUBBLE_GAP = 18; // distance between cutout and bubble
const BUBBLE_W = 320;

function rectFromEl(el: Element, padding: number): Rect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - padding,
    left: r.left - padding,
    width: r.width + padding * 2,
    height: r.height + padding * 2,
  };
}

function bubblePosition(side: Side, rect: Rect): {
  top: number; left: number; sideUsed: Side;
} {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Auto-flip side if there's no room on the preferred side.
  const roomBySide = {
    top: rect.top,
    bottom: vh - (rect.top + rect.height),
    left: rect.left,
    right: vw - (rect.left + rect.width),
  };
  let chosen: Side = side;
  if (roomBySide[side] < 180) {
    const order: Side[] = ["bottom", "right", "top", "left"];
    chosen = order.find(s => roomBySide[s] >= 180) || side;
  }
  let top = 0, left = 0;
  switch (chosen) {
    case "right":
      top = rect.top + rect.height / 2;
      left = rect.left + rect.width + BUBBLE_GAP;
      break;
    case "left":
      top = rect.top + rect.height / 2;
      left = rect.left - BUBBLE_GAP - BUBBLE_W;
      break;
    case "top":
      top = rect.top - BUBBLE_GAP;
      left = rect.left + rect.width / 2 - BUBBLE_W / 2;
      break;
    case "bottom":
      top = rect.top + rect.height + BUBBLE_GAP;
      left = rect.left + rect.width / 2 - BUBBLE_W / 2;
      break;
  }
  // Clamp horizontally.
  left = Math.max(VP_MARGIN, Math.min(left, vw - BUBBLE_W - VP_MARGIN));
  // Clamp vertically.
  const estBubbleH = 180;
  top = Math.max(VP_MARGIN, Math.min(top, vh - estBubbleH));
  return { top, left, sideUsed: chosen };
}

// ── Component ─────────────────────────────────────────────

export default function IntroTour() {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [bubble, setBubble] = useState<{ top: number; left: number; side: Side } | null>(null);
  const [missing, setMissing] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const step = STEPS[stepIdx];

  // Auto-start on first visit to /app/chat
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forced = params.get("tour") === "1";
    const onChat = pathname.startsWith("/app/chat");
    if ((forced || (onChat && !hasSeenIntroTour())) && !active) {
      const t = setTimeout(() => setActive(true), 700);
      return () => clearTimeout(t);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Programmatic start from Settings (or anywhere).
  useEffect(() => {
    const onStart = () => {
      setStepIdx(0);
      setActive(true);
    };
    window.addEventListener("foreman:intro-tour:start", onStart);
    return () => window.removeEventListener("foreman:intro-tour:start", onStart);
  }, []);

  // Navigate to the step's required route if not already there.
  useEffect(() => {
    if (!active || !step) return;
    if (step.goto && !pathname.startsWith(step.goto)) {
      navigate(step.goto);
    }
  }, [active, step, pathname, navigate]);

  // On mobile, the sidebar is hidden off-canvas. For any step whose
  // selector lives inside the sidebar, ask the shell to open it.
  // For chat-input (final step), ask the shell to close it.
  useEffect(() => {
    if (!active || !step) return;
    const wantsSidebarOpen = step.selector.includes("sidebar");
    window.dispatchEvent(
      new CustomEvent("foreman:sidebar:mobile-set", {
        detail: { open: wantsSidebarOpen },
      }),
    );
  }, [active, step]);

  // When the tour ends, close the mobile drawer so the user lands on
  // the actual chat surface.
  useEffect(() => {
    if (active) return;
    window.dispatchEvent(
      new CustomEvent("foreman:sidebar:mobile-set", {
        detail: { open: false },
      }),
    );
  }, [active]);

  // Locate and track the target. Uses rAF to re-read each frame —
  // expensive in theory, but on small overlays it's the only way to
  // guarantee pixel-exact tracking under arbitrary parent transforms,
  // scrolls, or layout shifts.
  const animFrame = useRef<number | null>(null);
  const updatePosition = useCallback(() => {
    if (!active || !step) return;
    const el = document.querySelector(step.selector);
    if (!el) {
      setMissing(true);
      setRect(null);
      setBubble(null);
      return;
    }
    setMissing(false);
    // Bring into view if needed.
    const r0 = el.getBoundingClientRect();
    const offscreen =
      r0.top < 0 ||
      r0.left < 0 ||
      r0.bottom > window.innerHeight ||
      r0.right > window.innerWidth;
    if (offscreen) {
      (el as HTMLElement).scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    }
    const padded = rectFromEl(el, step.padding ?? 6);
    setRect(padded);
    const b = bubblePosition(step.side, padded);
    setBubble({ top: b.top, left: b.left, side: b.sideUsed });
  }, [active, step]);

  useEffect(() => {
    if (!active || !step) return;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      updatePosition();
      animFrame.current = requestAnimationFrame(tick);
    };
    animFrame.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, [active, stepIdx, step, updatePosition]);

  // Listen for first keystroke on the chat input to end the tour.
  useEffect(() => {
    if (!active || !step?.isFinal) return;
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) return;
    const onInput = () => {
      complete();
    };
    el.addEventListener("input", onInput, true);
    return () => el.removeEventListener("input", onInput, true);
  }, [active, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc to skip.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll while the tour is active so the spotlight stays
  // locked to the target without scroll-jitter.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  const next = () => {
    if (stepIdx >= STEPS.length - 1) {
      complete();
    } else {
      setStepIdx(i => i + 1);
    }
  };
  const back = () => setStepIdx(i => Math.max(0, i - 1));
  const skip = () => {
    markSeen();
    setActive(false);
    setStepIdx(0);
  };
  const complete = () => {
    markSeen();
    setActive(false);
    setStepIdx(0);
  };

  // Memoise the SVG mask so resize+rAF don't thrash.
  const cutoutSvg = useMemo(() => {
    if (!rect) return null;
    const { top, left, width, height } = rect;
    const radius = step?.radius ?? 12;
    return (
      <svg className="it-cutout" aria-hidden width="100%" height="100%">
        <defs>
          <mask id="it-cutout-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={left}
              y={top}
              width={width}
              height={height}
              rx={radius}
              ry={radius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(20, 28, 16, 0.78)"
          mask="url(#it-cutout-mask)"
        />
        <rect
          x={left - 1}
          y={top - 1}
          width={width + 2}
          height={height + 2}
          rx={radius + 1}
          ry={radius + 1}
          fill="none"
          stroke="var(--accent, #b8e34f)"
          strokeWidth={2}
          className="it-ring"
        />
      </svg>
    );
  }, [rect, step]);

  if (!active || !step) return null;

  return (
    <div className="it-root" data-side={bubble?.side ?? "right"} role="dialog" aria-label="Welcome tour">
      {missing ? (
        // Target not in DOM yet (e.g., navigation in flight). Render a
        // dim overlay only — rAF will pick the rect up next frame.
        <div className="it-veil-fallback" />
      ) : (
        cutoutSvg
      )}

      {bubble && rect && (
        <div
          className="it-bubble"
          style={{ top: bubble.top, left: bubble.left, width: BUBBLE_W }}
          data-side={bubble.side}
          data-final={step.isFinal ? "true" : "false"}
        >
          <div className="it-bubble-head">
            <span className="it-avatar" aria-hidden>
              <span className="it-avatar-core" />
              <span className="it-avatar-ring" />
            </span>
            <span className="it-step-counter">
              {stepIdx + 1} / {STEPS.length}
            </span>
          </div>
          <div className="it-bubble-title">{step.title}</div>
          <div className="it-bubble-body">{step.body}</div>
          <div className="it-bubble-actions">
            <button type="button" className="it-skip" onClick={skip}>
              Skip tour
            </button>
            <div className="it-actions-right">
              {stepIdx > 0 && !step.isFinal && (
                <button type="button" className="it-back" onClick={back}>
                  Back
                </button>
              )}
              {step.isFinal ? (
                <span className="it-final-hint">
                  Start typing to begin
                </span>
              ) : (
                <button type="button" className="it-next" onClick={next}>
                  Got it →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
